import { ref, watch } from "vue"
// split out of components/Paging.js, which is now a `.vue`: a `<script setup>` block runs per
// instance and cannot carry a module-level composable alongside the component
export const usePaging = (updateFunc) => {
  if ((!updateFunc) instanceof Function) {
    throw TypeError("updateFunc must be async function")
  }

  const dirty = ref({ v: 0 })

  const paging = ref({
    numberOfRecords: null,
    pageNum: 1,
    itemsPerPage: 20,
    pages: 0,
  })

  const resetPaging = () => {
    paging.value.numberOfRecords = 0
    paging.value.pages = 0
    paging.value.pageNum = 1
    dirty.value.v++
  }

  const setPageNum = (n) => {
    if (!Number.isInteger(n)) {
      throw "setPageNum should be integer"
    }
    paging.value.pageNum = n
    dirty.value.v++
  }

  const setNumberOfRecords = (numberOfRecords) => {
    if (!Number.isInteger(numberOfRecords)) {
      throw "numberOfRecords should be integer"
    }
    paging.value.numberOfRecords = numberOfRecords
    paging.value.pages = Math.ceil(paging.value.numberOfRecords / paging.value.itemsPerPage)
  }

  // A change that arrives while a fetch is in flight is not lost - it runs once more as soon as
  // the fetch lands. Dropping it would leave the list showing the previous query's result (the
  // filters/page in the UI are already the new ones), which is exactly what live search hits.
  let fetching = false
  let changedWhileFetching = false
  watch(dirty.value, async () => {
    if (fetching) {
      changedWhileFetching = true
      return
    }
    fetching = true
    try {
      do {
        changedWhileFetching = false
        await updateFunc()
      } while (changedWhileFetching)
    } finally {
      fetching = false
    }
  })

  return {
    paging,
    resetPaging,
    setNumberOfRecords,
    setPageNum,
  }
}
